import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import TopBar from '../../../componentsv2/TopBar';
import MarcoLogo from '../../../componentsv2/MarcoLogo';
import Typography from '../../../componentsv2/Typography';
import { TextField } from '../../../componentsv2/Field';
import Button from '../../../componentsv2/Button';
import axios from '../../../services/axios';
import { useLazyTranslation } from '../../../hooks/i18n';
import { LOGIN, VERIFY_EMAIL_SENT } from '../../../constants/auth';
import { COLORS } from '../../../themev2';

const CreateAccountSchema = (t: any) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common:signup.form.email.errors.invalid'))
      .required(t('common:requiredField')),
  });

const CreateAccountUsingEmail = () => {
  const { t } = useLazyTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (
    values: { email: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setErrorMessage('');
    logEvent('Create Account Using Email - Clicked', { email: values.email });

    try {
      await axios.post(`/password/password-reset/`, { email: values.email });
      logEvent('Create Account Using Email - Success', { email: values.email });
      navigate(VERIFY_EMAIL_SENT, { state: { email: values.email } });
    } catch (error: any) {
      setErrorMessage(error.response?.data?.email);
      logEvent('Create Account Using Email - Failed', { email: values.email });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <TopBar>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton href="/">
            <MarcoLogo />
          </IconButton>
        </Box>
      </TopBar>
      <Box component="main" sx={{ flexGrow: 1, p: 8, backgroundColor: COLORS.gray[300] }}>
        <Container
          sx={{
            maxWidth: { xs: '100%', sm: 400, md: 500 },
            padding: '1em 0em',
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h4" textAlign={{ xs: 'center', md: 'left' }} fontWeight={700}>
                {t('createAccount.titleToConfirm')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                textAlign={{ xs: 'center', md: 'left' }}
                sx={{ fontSize: '18px', lineHeight: '27px', color: '#66758F' }}
              >
                {t('createAccount.subtitleToConfirm')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Formik
                initialValues={{ email: '' }}
                validationSchema={CreateAccountSchema(t)}
                onSubmit={handleSubmit}
                validateOnChange={false}
                validateOnBlur={true}
              >
                {({ values, handleSubmit, isSubmitting }) => (
                  <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          name="email"
                          label={t('reset.emailLabel')}
                          placeholder={t('reset.emailPlaceholder')}
                        />
                        {errorMessage && (
                          <Typography color="error" sx={{ mt: 1 }}>
                            {errorMessage}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sx={{ marginTop: '5em' }}>
                        <Button
                          fullWidth
                          color="secondary"
                          isLoading={isSubmitting}
                          disabled={!values.email || isSubmitting}
                          isSubmit
                        >
                          {t('createAccount.sendLinkToConfirm')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="outline"
                          color="tertiary"
                          fullWidth
                          onClick={() => {
                            logEvent('Create Account Using Email - Canceled');
                            navigate(LOGIN);
                          }}
                        >
                          {t('reset.cancel')}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default CreateAccountUsingEmail;
